import logger from '../utils/logger';
import axios from 'axios';
import { emitSubscriptionRequired } from '../utils/subscriptionEvents';
import { tokenStore } from '../utils/security';

// Environment-based API URL configuration
export const getAPIBaseURL = () => {
  // Check if we're in development mode
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  // Check for explicit API URL override
  if (process.env.REACT_APP_API_URL) {
    const apiUrl = process.env.REACT_APP_API_URL;
    return apiUrl.endsWith('/api') ? apiUrl : `${apiUrl}/api`;
  }
  
  // Development mode - use localhost:8080 (backend port)
  if (isDevelopment) {
    return 'http://localhost:8080/api';
  }
  
  // Production mode - use relative path (same domain)
  return '/api';
};

export const API_BASE_URL = getAPIBaseURL();

// Request deduplication cache
const pendingRequests = new Map();

const createRequestKey = (config) => {
  return `${config.method?.toUpperCase()}:${config.url}:${JSON.stringify(config.params || {})}`;
};

// Create axios instance with proper configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 8000, // 8 second timeout to prevent hanging
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = tokenStore.get();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const errorType = error.response?.data?.errorType;
    const isSub402 = status === 402 && errorType === 'SUBSCRIPTION_REQUIRED';
    if (!isSub402) {
      logger.error('API Error:', status, error.config?.url, error.message);
    }
    
    if (error.code === 'ECONNABORTED') {
      logger.error('Request timeout:', error);
    }
    
    if (status === 401) {
      // Soft handling: clear token & notify app without hard reload
      const alreadyOnLogin = window.location.pathname === '/login';
  tokenStore.clear();
      // Dispatch a custom event so AuthContext (or any listener) can logout & navigate via router
      window.dispatchEvent(new CustomEvent('auth-unauthorized'));
      if (!alreadyOnLogin) {
        // Use history push replacement through client routing by setting a flag
        // Fallback: mild redirect (still avoid full reload by using assign if needed) – but we skip to let router handle.
      }
      // Prevent further processing; promise rejection continues for caller-specific handling
    }
    
    if (isSub402) {
      const errorData = error.response?.data;
      emitSubscriptionRequired({
        subscriptionStatus: errorData.subscriptionStatus,
        expiryDate: errorData.expiryDate,
        message: errorData.error
      });
    }
    
    return Promise.reject(error);
  }
);

// Create a deduplicated request function
const deduplicatedRequest = async (config) => {
  const requestKey = createRequestKey(config);
  
  // If there's already a pending request with the same key, return it
  if (pendingRequests.has(requestKey)) {
    return pendingRequests.get(requestKey);
  }
  
  // Create new request
  const requestPromise = api.request(config);
  pendingRequests.set(requestKey, requestPromise);
  
  try {
    const response = await requestPromise;
    return response;
  } finally {
    // Remove from pending requests when done
    pendingRequests.delete(requestKey);
  }
};

// Override the main request methods to use deduplication
api.get = (url, config) => deduplicatedRequest({ ...config, method: 'GET', url });
api.post = (url, data, config) => deduplicatedRequest({ ...config, method: 'POST', url, data });
api.put = (url, data, config) => deduplicatedRequest({ ...config, method: 'PUT', url, data });
api.delete = (url, config) => deduplicatedRequest({ ...config, method: 'DELETE', url });

export default api;